/**
 * worldsSeo — the head strings for planet routes. Pure: no DOM, no window.
 *
 * gghere.com stays canonical for the city catalog (spec D2), so jubuddy.com/vienna
 * keeps working while handing that city's ranking to gghere. jubuddy.com/planet and
 * jubuddy.com/hk are the exception: they are jubuddy landers and keep self-canonicals
 * (ADR-0377). Routes outside the planet line return null and keep whatever
 * index.html gave them.
 */
import { WORLD_CARDS, sitemapPaths } from './worldsCatalog.ts';

export const PLANET_ORIGIN = 'https://gghere.com';
export const JUBUDDY_ORIGIN = 'https://jubuddy.com';
export const HUB_PATH = '/worlds';

/**
 * Which domain's name goes in the title (ADR-0274).
 *
 * Brand is a SEPARATE axis from canonical. City catalog routes stay canonical on
 * gghere.com (spec D2). jubuddy.com/planet and jubuddy.com/hk do not: a cross-domain
 * canonical was handing their ranking to gghere (ADR-0377). Unfurlers read og:title
 * and og:url out of raw HTML and never run our JS, so both are decided at build
 * time, per host — see `shellTargets()`.
 */
export type PlanetBrand = 'gghere' | 'jubuddy';

/** The default brand for any host without an explicit rewrite: preview deploys,
 *  play.jubit.ai, localhost. Also what every pre-ADR-0274 caller gets. */
export const DEFAULT_PLANET_BRAND: PlanetBrand = 'gghere';

/** Resolve the brand from a hostname. Only jubuddy.com and its subdomains rename;
 *  dot-anchored so `notjubuddy.com` cannot claim the brand. */
export function brandForHost(hostname: string): PlanetBrand {
  return /(^|\.)jubuddy\.com$/i.test(hostname) ? 'jubuddy' : DEFAULT_PLANET_BRAND;
}

/**
 * /worlds IS the gghere.com homepage (2026-08-03 direction): the marketing page
 * and the planet selector are one page. gghere.com/ 307s here — already live in
 * production, and far safer than the middleware path it replaced.
 */

/** Social card for the gghere catalog. WebP ≤200 KB; stays on gghere shells. */
const OG_IMAGE = `${PLANET_ORIGIN}/gghere-brand.webp`;
const OG_WIDTH = 1200;
const OG_HEIGHT = 800;

/**
 * Existing jubuddy-hosted city scene (960×320 WebP, about 53 KB).
 * Belt thumbs are 128×128, below a social card's useful size.
 */
export const JUBUDDY_PLANET_OG_IMAGE = `${JUBUDDY_ORIGIN}/assets/planet/story/harbour-quay-v1.webp`;
const JUBUDDY_PLANET_OG_WIDTH = 960;
const JUBUDDY_PLANET_OG_HEIGHT = 320;
const JUBUDDY_PLANET_OG_ALT = 'Hong Kong harbour at dusk, a walkable tiny-planet city on jubuddy';

export interface SeoHead {
  readonly canonical: string;
  readonly title: string;
  readonly description: string;
  readonly ogImage: string;
  readonly ogWidth?: number;
  readonly ogHeight?: number;
  readonly ogImageAlt?: string;
}

/**
 * gghere /planet collapses onto /hk (one Hong Kong URL on the catalog).
 * jubuddy /planet and /hk each keep the URL that was requested.
 */
export function canonicalForPlanetPath(path: string, brand: PlanetBrand): string {
  if (brand === 'jubuddy' && (path === '/planet' || path === '/hk')) {
    return `${JUBUDDY_ORIGIN}${path}`;
  }
  if (path === '/planet') return `${PLANET_ORIGIN}/hk`;
  return `${PLANET_ORIGIN}${path}`;
}

function withPlanetOg(
  path: string,
  brand: PlanetBrand,
  head: Omit<SeoHead, 'ogImage' | 'ogWidth' | 'ogHeight' | 'ogImageAlt'>,
): SeoHead {
  if (brand === 'jubuddy' && (path === '/planet' || path === '/hk')) {
    return {
      ...head,
      ogImage: JUBUDDY_PLANET_OG_IMAGE,
      ogWidth: JUBUDDY_PLANET_OG_WIDTH,
      ogHeight: JUBUDDY_PLANET_OG_HEIGHT,
      ogImageAlt: JUBUDDY_PLANET_OG_ALT,
    };
  }
  return { ...head, ogImage: OG_IMAGE, ogWidth: OG_WIDTH, ogHeight: OG_HEIGHT };
}

/**
 * One head tag to write. Every tag carries the `selector` that finds it, so the
 * applier is an overwrite-or-create loop and re-applying can never stack
 * duplicates. This project has no jsdom (components are tested through
 * renderToStaticMarkup), so keeping the tag PLAN pure is what makes the head
 * layer testable at all — see useSeoHead.ts.
 */
export type SeoTag =
  | { readonly kind: 'title'; readonly text: string }
  | { readonly kind: 'link'; readonly selector: string; readonly rel: string; readonly href: string }
  | {
      readonly kind: 'meta';
      readonly selector: string;
      readonly attr: 'name' | 'property';
      readonly key: string;
      readonly content: string;
    };

function metaTag(attr: 'name' | 'property', key: string, content: string): SeoTag {
  return { kind: 'meta', selector: `meta[${attr}="${key}"]`, attr, key, content };
}

/** The full set of head tags for a planet route, or null to leave the head alone. */
export function seoTags(
  pathname: string,
  brand: PlanetBrand = DEFAULT_PLANET_BRAND,
): SeoTag[] | null {
  const seo = seoForPath(pathname, brand);
  if (!seo) return null;

  return [
    { kind: 'title', text: seo.title },
    { kind: 'link', selector: 'link[rel="canonical"]', rel: 'canonical', href: seo.canonical },
    metaTag('name', 'description', seo.description),
    metaTag('property', 'og:url', seo.canonical),
    metaTag('property', 'og:title', seo.title),
    metaTag('property', 'og:description', seo.description),
    metaTag('property', 'og:image', seo.ogImage),
    metaTag('name', 'twitter:title', seo.title),
    metaTag('name', 'twitter:description', seo.description),
    metaTag('name', 'twitter:image', seo.ogImage),
  ];
}

/**
 * The head for `/planet` — the one planet URL whose SUBJECT is decided by a query
 * param rather than by its path (ADR-0228 publishes it as the inbound deep-link, and
 * `?district=` may name any world: Shinjuku, Île de la Cité, Lower Manhattan).
 *
 * Two things are deliberately different from a world route, and only two:
 *
 *  - **The title names no city.** This is why `shellParity.test.ts` refused `/planet`
 *    a prerendered shell at all: pointing it at `w/hk.html` would have stamped "Hong
 *    Kong as a tiny planet" onto every Tokyo arrival, which is worse than the flash it
 *    would have cured. A param-agnostic title can be prerendered honestly.
 *  - **The canonical depends on the host.** On gghere, a bare `/planet` is the same
 *    Hong Kong page as `/hk`, so it still points at `gghere.com/hk`. On jubuddy,
 *    `/planet` is the published play URL and `/hk` is its own lander; each
 *    canonical is that host plus that path (ADR-0377).
 */
export function planetShellHead(brand: PlanetBrand): SeoHead {
  const planets = WORLD_CARDS.reduce((n, c) => n + c.planetCount, 0);
  return withPlanetOg('/planet', brand, {
    canonical: canonicalForPlanetPath('/planet', brand),
    title: `Walk a real city as a tiny planet — ${brand}`,
    description: `Step onto any of ${planets} tiny planets built from real open map data. Walk it, ride the tram, drive a rally, run a delivery job. No account, runs in a browser tab.`,
  });
}

/**
 * 2026-08-25 — Route-specific SEO for non-worlds routes.
 *
 * Previously, ALL routes served the same canonical (/home) from index.html,
 * which merged SEO signals incorrectly. Each major surface now gets its own
 * canonical so search engines index them separately.
 */
const JUB_OG_IMAGE = `${JUBUDDY_ORIGIN}/video/homepage-hero.jpg`;

const ROUTE_SEO: Record<string, { title: string; description: string; canonical: string }> = {
  '/': {
    title: 'jubuddy — one little buddy, 40 anime styles',
    description: 'Render across 40 anime style families, chat with persona-grounded characters, play the keyword-fusion campaign. Same buddy, every style.',
    canonical: `${JUBUDDY_ORIGIN}/`,
  },
  '/home': {
    title: 'jubuddy — one little buddy, 40 anime styles',
    description: 'Render across 40 anime style families, chat with persona-grounded characters, play the keyword-fusion campaign. Same buddy, every style.',
    canonical: `${JUBUDDY_ORIGIN}/home`,
  },
  '/jub': {
    title: 'Jub Anime Studio — create anime portraits',
    description: 'Pick a subject, style, and stream preset. Generate anime portraits via Pollinations — no account required.',
    canonical: `${JUBUDDY_ORIGIN}/jub`,
  },
  '/studio': {
    title: 'Studio — 49 themes, persona-grounded prompts',
    description: 'Multi-universe prompt generator with 49 themes, 32 archetypes, and cross-remix. Server-composed prompts for maximum quality.',
    canonical: `${JUBUDDY_ORIGIN}/studio`,
  },
  '/imagine': {
    title: 'Imagine — text-to-image with HD + model picker',
    description: 'Generate images from prompts with quality presets, advanced model selection, and NSFW intensity options.',
    canonical: `${JUBUDDY_ORIGIN}/imagine`,
  },
  '/pictorial-book': {
    title: 'Pictorial Book — mythological creature encyclopedia',
    description: 'Browse series of mythological creatures with lore, vibes, and stream variants. Generate your own versions.',
    canonical: `${JUBUDDY_ORIGIN}/pictorial-book`,
  },
  '/chatlab': {
    title: 'ChatLab — chat with persona-grounded characters',
    description: 'Talk to LLM-powered characters with curated system prompts and portraits.',
    canonical: `${JUBUDDY_ORIGIN}/chatlab`,
  },
  '/apps': {
    title: 'Apps — all jubuddy surfaces',
    description: 'Browse all jubuddy surfaces: Jub Anime Studio, Studio Themes, Imagine, ChatLab, Pictorial Book, and more.',
    canonical: `${JUBUDDY_ORIGIN}/apps`,
  },
  '/play': {
    title: 'Play — keyword-fusion tower defense',
    description: 'Pick a hero, fuse keywords into towers, survive waves. Casual 2D tower defense with 9 mascot heroes.',
    canonical: `${JUBUDDY_ORIGIN}/play`,
  },
  '/poker': {
    title: "Jub Poker · 聚牌 — Hold'em, Blackjack, Baccarat vs bots",
    description:
      "Play-chip card tables: Texas Hold'em with side pots and GA bots, Blackjack, Baccarat, 45-Bust and Tic-Tac-Toe. No account, no real money.",
    canonical: `${JUBUDDY_ORIGIN}/poker`,
  },
  '/landing': {
    title: 'jubuddy — landing',
    description: 'Welcome to jubuddy. Start creating anime portraits or explore the platform.',
    canonical: `${JUBUDDY_ORIGIN}/landing`,
  },
};

export function seoForPath(
  pathname: string,
  brand: PlanetBrand = DEFAULT_PLANET_BRAND,
): SeoHead | null {
  // 2026-08-25 — Route-specific SEO for non-worlds routes.
  const routeSeo = ROUTE_SEO[pathname];
  if (routeSeo) {
    return {
      canonical: routeSeo.canonical,
      title: routeSeo.title,
      description: routeSeo.description,
      ogImage: JUB_OG_IMAGE,
    };
  }

  // Before normalize(): `/planet` keeps its own param-agnostic head rather than
  // borrowing Hong Kong's. Shell and client-side pass agree, so the head never
  // changes subject on mount.
  if (pathname === '/planet') return planetShellHead(brand);

  const path = pathname;

  if (path === HUB_PATH) {
    const cities = WORLD_CARDS.length;
    const planets = WORLD_CARDS.reduce((n, c) => n + c.planetCount, 0);
    return withPlanetOg(HUB_PATH, brand, {
      canonical: canonicalForPlanetPath(HUB_PATH, brand),
      title: `${cities} real cities, rebuilt as tiny planets you can walk — ${brand}`,
      description: `${planets} tiny planets across ${cities} real cities, built from open map data — 4.5 million real building footprints. Walk them, drive them, or take a delivery job. No account, runs in a browser tab.`,
    });
  }

  const card = WORLD_CARDS.find((c) => c.path === path);
  if (!card) return null;

  return withPlanetOg(path, brand, {
    canonical: canonicalForPlanetPath(path, brand),
    title: `${card.place} as a tiny planet — ${brand}`,
    description: `${card.place} rebuilt from open map data as ${card.planetCount} planets you can walk, drive and play. Explore, Courier Rush, Survival and Coin Rally.`,
  });
}

/** /landing redirects to /home, so it must not be listed as its own loc. */
const SITEMAP_REDIRECTS = new Set(['/landing']);

/**
 * jubuddy.com URLs that should rank: the marketing home, the two planet landers,
 * and every other route that already publishes a self-canonical.
 */
export function jubuddySitemapPaths(): string[] {
  const rest = Object.keys(ROUTE_SEO).filter((p) => p !== '/' && !SITEMAP_REDIRECTS.has(p));
  return ['/', '/planet', '/hk', ...rest];
}

export function gghereSitemapLocs(): string[] {
  return sitemapPaths().map((p) => `${PLANET_ORIGIN}${p}`);
}

export function jubuddySitemapLocs(): string[] {
  return jubuddySitemapPaths().map((p) => `${JUBUDDY_ORIGIN}${p}`);
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** ISO date for sitemap <lastmod>; box/ship day Asia/Shanghai (UTC+8). */
const SITEMAP_LASTMOD = '2026-10-26';

export function renderUrlset(locs: readonly string[]): string {
  const urls = locs
    .map(
      (loc) =>
        `  <url>\n    <loc>${escapeXml(loc)}</loc>\n    <lastmod>${SITEMAP_LASTMOD}</lastmod>\n  </url>`,
    )
    .join('\n');
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}\n</urlset>\n`;
}

export function robotsTxtFor(origin: string): string {
  return `User-agent: *\nAllow: /\nDisallow: /api/\nDisallow: /admin/\n\nSitemap: ${origin}/sitemap.xml\n`;
}
